import React, { useMemo } from "react";
import { Modal } from "react-bootstrap";
import styled from "styled-components";
import { Button } from "../form/Button";
import CrossIcon from "../../assets/icons/CrossIcon";
import DocumentIcon from "../../assets/icons/document-text";
import AddIcon from "../../assets/icons/AddIcon";
import { FieldArray, Form, Formik, useFormikContext } from "formik";
import * as Yup from "yup";
import { useTheme } from "../../middleware/ThemeContext";
import { useGetBulkUpdateFieldsQuery, useBulkUpdateFieldsMutation } from "../../api/searchQueryApiSlice";
import CustomTextArea from "../form/CustomTextArea";
import { CheckboxItem } from "../listing/ColumnIdentifier";
import MultiSelectDropdown from "../form/MultiSelectDropdown";
import CustomInput from "../form/CustomInput";
import { useToast } from "../toast/ToastContext";
import CalendarIcon from "../../assets/icons/CalendarIcon";

import ModalHeaderBg from "../../assets/images/CreateFormBackground.png";
import { font } from "../../utils/themes";
import StyledText from "../StyledComponents/StyledText";
import DeleteIcon from "../../assets/icons/DeleteIcon";
import SaveIcon from "../../assets/icons/SaveIcon";
import LoadingCircleIcon from "../../assets/icons/LoadingCircleIcon";
import { LayoutWidget } from "../../features/contacts/ContactOverview/DND/dndTypes";
import { loadGoogleMapsScript } from '../../utils/googleMapsLoader';
import FieldControl from "../FormBuilder/FieldPreviews/FieldControl";
import { getRegionCodeForCountryName, normalizeCallingCode, PhoneErrorCode, PhoneValidationOptions, validatePhoneNumber } from "../../utils/phoneValidation";
import { buildPhoneDependencyMap } from "../../utils/phoneFieldDependencies";
interface WidgetUpdateModalProps {
    show: boolean;
    onClose: () => void;
    scope: string;
    recordIds: number[];
    refetch?: () => void;
    prefillData?: LayoutWidget;
    userDetailsRefetch?: () => void,
    refetchOrganizationData?: () => void,
}

interface FieldUpdate {
    id: string;
    field: string;
    value: any;
    name?: string;
}


interface FormValues {
    updates: FieldUpdate[];
}

type AddressScope = 'home' | 'work' | 'other' | 'mailing' | 'shipping' | 'billing' | 'primary' | 'general';
type AddressPart = 'line1' | 'line2' | 'city' | 'state' | 'zip' | 'country';

type AddressPartsSnapshot = Record<AddressPart, string>;

const DIRECTORY_CHECKBOX_ACCESS_KEYS = new Set([
    'contact_work_address_directory',
    'contact_home_address_directory',
    'contact_other_address_directory',
]);

const isBooleanFieldType = (field: any): boolean => {
    const dataType = String(field?.dataType || '').toUpperCase();
    const fieldType = String(field?.fieldType || '').toUpperCase();
    return dataType === 'BOOLEAN' || dataType === 'CHECKBOX' || fieldType === 'BOOLEAN' || fieldType === 'CHECKBOX';
};

const normalizeBooleanValue = (rawValue: any): boolean => {
    if (rawValue === true) return true;
    if (rawValue === false || rawValue === undefined || rawValue === null) return false;

    if (typeof rawValue === 'number') {
        return rawValue === 1;
    }

    if (typeof rawValue === 'string') {
        const normalized = rawValue.trim().toLowerCase();
        return normalized === 'true' || normalized === '1' || normalized === 'yes' || normalized === 'y' || normalized === 'on';
    }

    return Boolean(rawValue);
};

interface AddressAutoFillPayload {
    formattedAddress: string;
    components: Record<string, string>;
    value: string;
    location?: { lat: number; lng: number };
}

const resolveCountryCallingCode = (rawValue: any): string | undefined => {
    if (typeof rawValue === 'string') {
        return normalizeCallingCode(rawValue) || undefined;
    }

    if (rawValue && typeof rawValue === 'object') {
        if ('value' in rawValue && rawValue.value != null) {
            return normalizeCallingCode(String(rawValue.value ?? '')) || undefined;
        }
        if ('label' in rawValue && rawValue.label != null) {
            return normalizeCallingCode(String(rawValue.label ?? '')) || undefined;
        }
    }

    return undefined;
};

const resolveCountryRegionCode = (rawValue: any): string | undefined => {
    if (typeof rawValue === 'string') {
        return getRegionCodeForCountryName(rawValue) || undefined;
    }

    if (rawValue && typeof rawValue === 'object') {
        if ('value' in rawValue && rawValue.value != null) {
            return getRegionCodeForCountryName(String(rawValue.value ?? '')) || undefined;
        }
        if ('label' in rawValue && rawValue.label != null) {
            return getRegionCodeForCountryName(String(rawValue.label ?? '')) || undefined;
        }
    }

    return undefined;
};

const resolveCountryNameText = (rawValue: any): string | undefined => {
    if (typeof rawValue === 'string') {
        const trimmed = rawValue.trim();
        return trimmed ? trimmed : undefined;
    }

    if (rawValue && typeof rawValue === 'object') {
        if ('value' in rawValue && rawValue.value != null) {
            const text = String(rawValue.value ?? '').trim();
            return text ? text : undefined;
        }
        if ('label' in rawValue && rawValue.label != null) {
            const text = String(rawValue.label ?? '').trim();
            return text ? text : undefined;
        }
    }

    return undefined;
};

const deriveAddressPartsFromComponents = (
    components: Record<string, string>,
    formattedAddress: string,
    fallbackLine1?: string
): AddressPartsSnapshot => {
    const streetNumber = components.street_number || '';
    const route = components.route || '';
    const line1 = [streetNumber, route].filter(Boolean).join(' ').trim() || fallbackLine1 || formattedAddress || '';
    const line2 = components.subpremise || components.floor || '';
    const city = components.locality || components.sublocality || components.administrative_area_level_2 || '';
    const state = components.administrative_area_level_1 || components.administrative_area_level_2 || '';
    const zip = components.postal_code || components.postal_code_suffix || '';
    const country = components.country || '';
    return { line1, line2, city, state, zip, country };
};

const detectAddressScopeFromField = (field: any): AddressScope => {
    const haystack = `${field?.label || ''} ${field?.name || ''} ${field?.accessKey || ''}`.toLowerCase();
    if (haystack.includes('home')) return 'home';
    if (haystack.includes('work') || haystack.includes('office')) return 'work';
    if (haystack.includes('billing')) return 'billing';
    if (haystack.includes('shipping')) return 'shipping';
    if (haystack.includes('mail')) return 'mailing';
    if (haystack.includes('primary')) return 'primary';
    if (haystack.includes('other')) return 'other';
    return 'general';
};

const detectAddressPartFromField = (field: any): AddressPart | null => {
    const typeToken = String(field?.fieldType || field?.type || '').toUpperCase();
    const label = `${field?.label || ''} ${field?.name || ''}`.toLowerCase();
    const key = String(field?.accessKey || '').toLowerCase();
    const haystack = `${label} ${key}`;
    const contains = (token: string) => haystack.includes(token);

    if (typeToken.includes('LINE_2') || contains('line 2') || contains('line2') || contains('suite')) {
        return 'line2';
    }
    if (typeToken.includes('CITY') || contains('city') || contains('town')) {
        return 'city';
    }
    if (
        typeToken.includes('STATE') ||
        contains('state') ||
        contains('province') ||
        contains('region')
    ) {
        return 'state';
    }
    if (
        typeToken.includes('ZIP') ||
        typeToken.includes('POSTAL') ||
        contains('zip') ||
        contains('postal') ||
        contains('postcode')
    ) {
        return 'zip';
    }
    if (typeToken.includes('COUNTRY') || contains('country')) {
        return 'country';
    }
    if (
        typeToken.includes('ADDRESS') ||
        contains('line 1') ||
        contains('line1') ||
        contains('address') ||
        contains('street')
    ) {
        return 'line1';
    }
    return null;
};

const WidgetAddressAutoFillListener: React.FC<{ widgetFields: any[] }> = ({ widgetFields }) => {
    const { values, setFieldValue } = useFormikContext<FormValues>();
    const addressIndexRef = React.useRef<Record<string, Partial<Record<AddressPart, string>>>>({});
    const updateIndexByAccessKeyRef = React.useRef<Map<string, number>>(new Map());
    const widgetFieldsRef = React.useRef<any[]>(widgetFields);

    React.useEffect(() => {
        widgetFieldsRef.current = widgetFields;

        const nextIndex: Record<string, Partial<Record<AddressPart, string>>> = {};
        for (const wrapper of widgetFields || []) {
            const f = (wrapper as any)?.field ?? wrapper;
            if (!f?.accessKey) continue;

            if (DIRECTORY_CHECKBOX_ACCESS_KEYS.has(String(f.accessKey))) continue;
            if (isBooleanFieldType(f)) continue;

            const part = detectAddressPartFromField(f);
            if (!part) continue;

            const scope = detectAddressScopeFromField(f);
            if (!nextIndex[scope]) nextIndex[scope] = {};
            nextIndex[scope][part] = f.accessKey;
        }
        addressIndexRef.current = nextIndex;
    }, [widgetFields]);

    React.useEffect(() => {
        const m = new Map<string, number>();
        (values?.updates || []).forEach((u, idx) => {
            if (u?.field) m.set(u.field, idx);
        });
        updateIndexByAccessKeyRef.current = m;
    }, [values?.updates]);

    React.useEffect(() => {
        const handler = (e: Event) => {
            const detail = (e as CustomEvent)?.detail;
            if (!detail) return;

            const components = detail.components as Record<string, string> | undefined;
            const formattedAddress = String(detail.formattedAddress || detail.value || '');
            if (!components) return;

            let scope: AddressScope | 'unknown' = (detail.scope || 'unknown') as any;
            const sourceFieldId = detail.sourceFieldId as number | undefined;

            // If FieldControl couldn't determine scope, infer it from the source field.
            if (scope === 'unknown' && typeof sourceFieldId === 'number') {
                const wf = widgetFieldsRef.current || [];
                const found = wf
                    .map((w) => (w as any)?.field ?? w)
                    .find((f: any) => f?.id === sourceFieldId);
                if (found) {
                    scope = detectAddressScopeFromField(found);
                }
            }

            const addressIndex = addressIndexRef.current;
            const scopeIndex = addressIndex[scope] || addressIndex.general;
            if (!scopeIndex) return;

            const parts = deriveAddressPartsFromComponents(components, formattedAddress, formattedAddress);
            const updateIndexByAccessKey = updateIndexByAccessKeyRef.current;

            (Object.keys(scopeIndex) as AddressPart[]).forEach((part) => {
                const accessKey = scopeIndex[part];
                if (!accessKey) return;
                const updateIdx = updateIndexByAccessKey.get(accessKey);
                if (updateIdx === undefined) return;
                setFieldValue(`updates.${updateIdx}.value`, parts[part]);
            });
        };

        window.addEventListener('AMS_ADDRESS_AUTOFILL', handler as EventListener);
        return () => window.removeEventListener('AMS_ADDRESS_AUTOFILL', handler as EventListener);
    }, [setFieldValue]);

    return null;
};

const WidgetUpdateModal: React.FC<WidgetUpdateModalProps> = ({
    show,
    onClose,
    scope = "contact",
    recordIds = [],
    refetch,
    prefillData,
    userDetailsRefetch,
    refetchOrganizationData
}) => {
    const showToast = useToast();
    const { theme } = useTheme();
    const { data: fields = [], isLoading } = useGetBulkUpdateFieldsQuery(scope);
    const [updateFields, { isLoading: isUpdating }] = useBulkUpdateFieldsMutation();
    const widgetPhoneFieldDependencies = useMemo(() => buildPhoneDependencyMap(prefillData?.widget?.fields || []), [prefillData?.widget?.fields]);
    const widgetFieldByAccessKey = useMemo(() => {
        const map = new Map<string, any>();
        (prefillData?.widget?.fields || []).forEach((wrapper: any) => {
            const field = wrapper?.field;
            if (field?.accessKey) {
                map.set(field.accessKey, field);
            }
        });
        return map;
    }, [prefillData?.widget?.fields]);
    const [fieldErrors, setFieldErrors] = React.useState<Record<string, string | null>>({});

    React.useEffect(() => {
        setFieldErrors({});
    }, [prefillData?.widget?.fields, show]);

    const handleFieldValidationChange = React.useCallback((updateId: string, error: string | null) => {
        setFieldErrors((prev) => {
            const next = { ...prev };
            if (!error) {
                if (updateId in next) {
                    delete next[updateId];
                }
                return next;
            }
            next[updateId] = error;
            return next;
        });
    }, []);

    const runPhoneValidation = React.useCallback((updates: FieldUpdate[]) => {
        const errors: Record<string, string> = {};

        updates.forEach((update) => {
            const field = widgetFieldByAccessKey.get(update.field);
            if (!field?.id) return;

            const dependencyInfo = widgetPhoneFieldDependencies.get(field.id);
            if (!dependencyInfo) return;

            const rawValue = update.value;
            let normalizedPhoneValue: string | undefined;
            if (typeof rawValue === "string") {
                normalizedPhoneValue = rawValue.trim();
            } else if (rawValue && typeof rawValue === "object") {
                if ("value" in rawValue && rawValue.value != null) {
                    normalizedPhoneValue = String(rawValue.value ?? "").trim();
                } else if ("label" in rawValue && rawValue.label != null) {
                    normalizedPhoneValue = String(rawValue.label ?? "").trim();
                }
            }

            if (!normalizedPhoneValue) {
                return;
            }

            const linkedValueEntry = dependencyInfo.countryFieldAccessKey
                ? updates.find((candidate) => candidate.field === dependencyInfo.countryFieldAccessKey)
                : undefined;
            const countryCodeEntry = dependencyInfo.countryCodeFieldAccessKey
                ? updates.find((candidate) => candidate.field === dependencyInfo.countryCodeFieldAccessKey)
                : undefined;
            const countryNameEntry = dependencyInfo.countryNameFieldAccessKey
                ? updates.find((candidate) => candidate.field === dependencyInfo.countryNameFieldAccessKey)
                : undefined;
            const fallbackCountryName = resolveCountryNameText(countryNameEntry?.value ?? linkedValueEntry?.value);

            const phoneValidationOptions: PhoneValidationOptions = {
                countryCallingCode: resolveCountryCallingCode(countryCodeEntry?.value ?? linkedValueEntry?.value),
                countryCode: resolveCountryRegionCode(countryNameEntry?.value ?? linkedValueEntry?.value) || fallbackCountryName,
                countryFieldLabel: dependencyInfo.countryFieldLabel,
            };

            const validationResult = validatePhoneNumber(normalizedPhoneValue, phoneValidationOptions);
            if (!validationResult.isValid) {
                let message = validationResult.message || "Invalid phone number";
                if (
                    validationResult.errorCode === PhoneErrorCode.PHONE_COUNTRY_REQUIRED &&
                    dependencyInfo.countryFieldLabel
                ) {
                    message = `${dependencyInfo.countryFieldLabel} is required when phone number is provided`;
                }
                errors[update.id] = message;
            }
        });

        return errors;
    }, [widgetFieldByAccessKey, widgetPhoneFieldDependencies]);

    const initialValues: FormValues = useMemo(() => {
        const updates: FieldUpdate[] = [];

        if (prefillData?.widget?.fields) {
            for (const wrapper of prefillData.widget.fields) {
                const f = wrapper.field;

                if (!f || !f.accessKey || !f.dataType || f.accessKey === null) {
                    continue;
                }

                let processedValue: any;

                if (DIRECTORY_CHECKBOX_ACCESS_KEYS.has(f.accessKey) || isBooleanFieldType(f)) {
                    processedValue = normalizeBooleanValue(f.value);
                    updates.push({
                        id: `prefill-${f.accessKey}`,
                        field: f.accessKey,
                        value: processedValue,
                        name: f.name
                    });
                    continue;
                }

                // Handle null/undefined first
                if (f.value == null || f.value === "") {
                    processedValue = null;

                    // But for non-nullable fields like text, use empty string
                    if (!["NUMBER", "DECIMAL", "CURRENCY", "DATE", "DATE_TIME", "BOOLEAN", "CHECKBOX"].includes(f.dataType)) {
                        processedValue = "";
                    }
                } else {
                    const rawValue = f.value as any;

                    // f.value is not null/undefined
                    if (f.dataType === "MULTIPLE_CHOICES") {
                        if (rawValue == null || rawValue === "") {
                            processedValue = [];
                        } else if (Array.isArray(rawValue)) {
                            processedValue = rawValue;
                        } else if (typeof rawValue === "string") {
                            processedValue = rawValue.split(",").map(s => s.trim()).filter(s => s !== "");
                        } else {
                            processedValue = [];
                        }
                    }
                    else if (["BOOLEAN", "CHECKBOX"].includes(f.dataType)) {
                        const s = typeof f.value === "string" ? f.value.trim().toLowerCase() : "";
                        processedValue =
                            f.value === true ||
                            f.value === 1 ||
                            f.value === "1" ||
                            s === "true" ||
                            s === "yes" ||
                            s === "y" ||
                            s === "on";
                    }
                    else if (f.dataType === "NUMBER") {
                        const num = Number(f.value);
                        processedValue = isNaN(num) ? null : num;
                    }
                    else if (["DECIMAL", "CURRENCY"].includes(f.dataType)) {
                        const num = parseFloat(String(f.value));
                        processedValue = isNaN(num) ? null : num;
                    }
                    else if (["DATE", "DATE_TIME"].includes(f.dataType)) {
                        processedValue = f.value; // assume backend sends valid string
                    }
                    else if (["FILE_UPLOAD", "IMAGE_UPLOAD"].includes(f.dataType)) {
                        processedValue = f.value;
                    }
                    else {
                        // SINGLE_LINE, MULTI_LINE, SELECT, RADIO, etc.
                        processedValue = String(f.value);
                    }
                }
                // else: keep as-is (string, etc.)

                updates.push({
                    id: `prefill-${f.accessKey}`,
                    field: f.accessKey,
                    value: processedValue, // ✅ ab TypeScript ko pata hai ke value: any hai
                    name: f.name
                });
            }
        }

        return {
            updates: updates.length > 0 ? updates : [{ id: Date.now().toString(), field: "", value: "" }]
        };
    }, [prefillData]);

    const formatValueForPayload = (value: any): any => {
        if (typeof value === 'boolean') {
            return value;
        }

        if (typeof value === 'number') {
            return value;
        }

        if (Array.isArray(value)) {
            const normalized = value
                .map((item) => {
                    if (item == null) return '';
                    if (typeof item === 'object') {
                        const candidate = (item as any).label ?? (item as any).value ?? (item as any).name;
                        return candidate !== undefined ? String(candidate) : JSON.stringify(item);
                    }
                    return String(item);
                })
                .filter(Boolean);
            return normalized.join(', ');
        }

        if (value && typeof value === 'object') {
            return JSON.stringify(value);
        }

        if (value === undefined || value === null) return '';

        return value.toString();
    };

    const handleSubmit = async (values: typeof initialValues) => {
        const phoneErrors = runPhoneValidation(values.updates);
        const nextFieldErrors = { ...fieldErrors };

        values.updates.forEach((update) => {
            const field = widgetFieldByAccessKey.get(update.field);
            if (field?.id && widgetPhoneFieldDependencies.has(field.id)) {
                delete nextFieldErrors[update.id];
            }
        });

        Object.entries(phoneErrors).forEach(([updateId, message]) => {
            nextFieldErrors[updateId] = message;
        });

        const hasErrors = Object.values(nextFieldErrors).some((message) => Boolean(message));
        if (hasErrors) {
            setFieldErrors(nextFieldErrors);
            showToast('Validation Error', 'Please fix the highlighted phone fields before saving.', 'error');
            return;
        }

        setFieldErrors(nextFieldErrors);

        try {
            const payload = {
                fields: values.updates
                    .filter(({ value }) => value !== undefined && value !== null && value !== '')
                    .filter(({ value }) => !(Array.isArray(value) && value.length === 0))
                    .map(({ field, value }) => ({
                        fieldAccessKey: field,
                        value: formatValueForPayload(value),
                    })),
                scope,
                recordIds,
            };

            await updateFields(payload).unwrap();
            showToast('Bulk Update Successful', 'Fields updated successfully.', 'success');
            refetch && refetch();
            userDetailsRefetch && userDetailsRefetch();
            refetchOrganizationData && refetchOrganizationData();
            onClose();
        } catch (error: any) {
            
            const errorMessage =
                error?.data?.message ||
                error?.error ||
                'Failed to update fields. Please try again.';

            showToast('Bulk Update Failed', errorMessage, 'error');
        }
    };

    const renderValueField = (
        selectedField: any,
        index: number,
        setFieldValue: (field: string, value: any) => void,
        currentValue: any,
        error: string | undefined,
        allUpdates: FormValues["updates"],
        updateId: string,
    ) => {
        if (!selectedField) return null;

        let phoneValidationOptions: PhoneValidationOptions | undefined;
        if (selectedField?.id) {
            const dependencyInfo = widgetPhoneFieldDependencies.get(selectedField.id);
            if (dependencyInfo) {
                const linkedValueEntry = dependencyInfo.countryFieldAccessKey
                    ? allUpdates.find((update) => update.field === dependencyInfo.countryFieldAccessKey)
                    : undefined;
                const countryCodeEntry = dependencyInfo.countryCodeFieldAccessKey
                    ? allUpdates.find((update) => update.field === dependencyInfo.countryCodeFieldAccessKey)
                    : undefined;
                const countryNameEntry = dependencyInfo.countryNameFieldAccessKey
                    ? allUpdates.find((update) => update.field === dependencyInfo.countryNameFieldAccessKey)
                    : undefined;
                const resolvedCountryValue = resolveCountryCallingCode(countryCodeEntry?.value ?? linkedValueEntry?.value);
                const resolvedCountryCode = resolveCountryRegionCode(countryNameEntry?.value ?? linkedValueEntry?.value);
                const fallbackCountryName = resolveCountryNameText(countryNameEntry?.value ?? linkedValueEntry?.value);

                phoneValidationOptions = {
                    countryCallingCode: resolvedCountryValue,
                    countryCode: resolvedCountryCode || fallbackCountryName,
                    countryFieldLabel: dependencyInfo.countryFieldLabel,
                };
            }
        }

        const combinedError = fieldErrors[updateId] || error;

        return (
            <FieldControl
                key={selectedField?.accessKey || selectedField?.id}
                field={selectedField}
                isRequired={selectedField?.config?.uiMeta?.isRequired}
                value={currentValue}
                onChange={(value: any) => setFieldValue(`updates.${index}.value`, value)}
                error={combinedError}
                phoneValidationOptions={phoneValidationOptions}
                onValidationChange={(validationError) => handleFieldValidationChange(updateId, validationError)}
            />);
    };

    return (
        <Modal show={show} onHide={onClose} size="lg" centered>
            <StyledModalContent theme={theme}>
                <ModalHeader>
                    <ModalTitle>
                        <StyledText textColor={theme.CorporateTheme?.colors?.primary} fontWeight={600} fontSize="20px" fontFamily={font.raleway}>{prefillData?.widget?.name}</StyledText>
                        <StyledText
                            fontSize="14px"
                            fontWeight={500}
                            textColor={theme.CorporateTheme?.colors?.textSecondary}
                            lineHeight="1.5"
                            className="mt-2"
                        >
                            Manage and view all {prefillData?.widget?.name} associated with this widget
                        </StyledText>
                    </ModalTitle>
                </ModalHeader>
                <Formik
                    enableReinitialize
                    initialValues={initialValues}
                    onSubmit={handleSubmit}
                >
                    {({ values, setFieldValue, errors, touched }) => (
                        <Form>
                            <WidgetAddressAutoFillListener widgetFields={prefillData?.widget?.fields || []} />
                            <ModalBody >
                                <div className=" ps-4   ms-4 me-5">
                                    <div className="d-flex flex-column rounded gap-4 py-4 ps-4" style={{border: `0.5px solid ${theme.CorporateTheme.colors.border}`, maxHeight: values?.updates?.length > 5 ? '430px' : 'auto', overflowY: values?.updates?.length > 5 ? 'auto' : 'visible', }}>
                                        <div className="d-flex flex-column gap-4" style={{ maxHeight: values?.updates?.length > 5 ? '430px' : 'auto', overflowY: values?.updates?.length > 5 ? 'auto' : 'visible', }}>

                                            <>
                                                {values.updates.map((update, index) => {
                                                    const widgetField = prefillData?.widget?.fields
                                                        ?.find((f: any) => f?.field?.accessKey === update.field)
                                                        ?.field;
                                                    const valueError = (errors.updates as any)?.[index]?.value;
                                                    const valueTouched = (touched.updates as any)?.[index]?.value;

                                                    return (


                                                        < div key={update.id} className="d-flex flex-column gap-2 w-100" >
                                                            <StyledText fontSize="14px" fontWeight={600} textColor={theme.CorporateTheme.colors.headerColor} className="mb-1">{values.updates[index].name}</StyledText>

                                                            <FieldWrapper className="pe-4">
                                                                {widgetField
                                                                    ? renderValueField(
                                                                        widgetField,
                                                                        index,
                                                                        setFieldValue,
                                                                        update?.value,
                                                                        valueError && valueTouched ? valueError : undefined,
                                                                        values.updates,
                                                                        update.id,
                                                                    )
                                                                    : (
                                                                        <FieldContainer>
                                                                            <CustomInput
                                                                                placeholder="Select a field first"
                                                                                value="Select a field first"
                                                                                onChange={() => { }}
                                                                                disabled
                                                                            />
                                                                        </FieldContainer>
                                                                    )}
                                                            </FieldWrapper>
                                                        </div>
                                                    );
                                                })}

                                                {typeof errors.updates === 'string' ? (
                                                    <ErrorText style={{ marginBottom: '16px' }}>{errors.updates}</ErrorText>
                                                ) : null}

                                            </>

                                        </div>
                                    </div>
                                    <div className="d-flex align-items-center justify-content-end mt-4">
                                        <div className="d-flex gap-1">
                                            <Button
                                                theme={theme}
                                                type="button"
                                                className="cursor-pointer"
                                                $variant="cancel"
                                                onClick={onClose}
                                                disabled={isUpdating}
                                            >
                                                <CrossIcon />
                                                Cancel
                                            </Button>
                                            <Button
                                                theme={theme}
                                                type="submit"
                                                $variant="add"
                                                className="cursor-pointer"
                                                disabled={isUpdating}
                                            >
                                                <div className="d-flex align-items-center gap-1">
                                                    {isUpdating ? (
                                                        <>
                                                            <LoadingCircleIcon color={theme.CorporateTheme.colors.background} />
                                                            <span>Save</span>
                                                            {/* Add a loading spinner if available */}
                                                        </>
                                                    ) : (
                                                        <>
                                                            <SaveIcon color={theme.CorporateTheme.colors.background} />
                                                            <label className="cursor-pointer">Save</label>
                                                        </>
                                                    )}
                                                </div>
                                            </Button>
                                        </div>
                                    </div>
                                </div>
                            </ModalBody>
                        </Form>
                    )}
                </Formik>
                <ModalFooter></ModalFooter>
            </StyledModalContent>
        </Modal >
    );
};

export default WidgetUpdateModal;

const StyledModalContent = styled.div`
  border-radius: 6px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.15);
  background: ${({ theme }) => theme.colors.contentBackground};
`;

const ModalHeader = styled.div`
  position: relative;
  padding-bottom: 30px;
  background-image: url(${ModalHeaderBg});
  background-size: cover;
  background-position: center;
background-position: 0px -56px;
`;

const ModalTitle = styled.div`
  text-align: center;
  padding: 0 30px;
  margin-top: 30px;
`;

const ModalBody = styled.div`
  padding-top: 0;
`;


const ModalFooter = styled.div`
  display: flex;
  justify-content: center;
  padding: 30px;
`;


const FieldWrapper = styled.div`
  display: flex;
  flex-direction: column;
`;

const FieldContainer = styled.div <{ minHeight?: string }>`
  position: relative;

  min-height: ${({ minHeight }) => minHeight || '42px'}; /* Fixed height for input fields */
`;

const ErrorText = styled.div`
  color: #dc3545;
  font-size: 16px;
  left: 0;
  right: 0;
  min-height: 16px;
`;

;



interface AddressAutocompleteBUProps {
    value: string | { formattedAddress?: string; value?: string };
    onChange: (value: any) => void;
    placeholder?: string;
    disabled?: boolean;
    mode?: 'address-line' | 'city' | 'state' | 'postal-code';
    onAutoFill?: (payload: AddressAutoFillPayload) => void;
}

const AutocompleteInput = styled.input<{ theme: any }>`
    padding: 10px 12px;
    border-radius: 5px;
    border: 1px solid ${({ theme }) => theme.CreateFormTheme?.colors?.inputBorders};
    background-color: ${({ theme }) => theme.CreateFormTheme?.colors?.inputBackground};
    color: ${({ theme }) => theme.CreateFormTheme?.colors?.inputColor};
    width: 100%;

    &::placeholder {
        color: ${({ theme }) => theme.CreateFormTheme?.colors?.placeHolder};
    }

    &:disabled {
        background-color: ${({ theme }) => theme.CreateFormTheme?.colors?.inputBackground};
        cursor: not-allowed;
        color: ${({ theme }) => theme.CreateFormTheme?.colors?.placeHolder};
    }

    &:focus {
        border-color: #40a9ff;
        box-shadow: 0 0 0 2px rgba(24, 144, 255, 0.2);
        outline: none;
    }
`;

const AddressAutocompleteBU: React.FC<AddressAutocompleteBUProps> = ({
    value,
    onChange,
    placeholder,
    disabled,
    mode = 'address-line',
    onAutoFill,
}) => {
    const { theme } = useTheme();
    const inputRef = React.useRef<HTMLInputElement | null>(null);
    const autocompleteRef = React.useRef<any>(null);
    const [initError, setInitError] = React.useState<string | null>(null);
    const autoFillCallbackRef = React.useRef<AddressAutocompleteBUProps['onAutoFill'] | undefined>(undefined);

    React.useEffect(() => {
        autoFillCallbackRef.current = onAutoFill;
    }, [onAutoFill]);

    React.useEffect(() => {
        let cancelled = false;
        loadGoogleMapsScript()
            .then(() => {
                if (cancelled) return;
                const google: any = (window as any).google;
                if (!google?.maps?.places || !inputRef.current) {
                    setInitError('Google Maps Places library not available');
                    return;
                }
                setInitError(null);
                const options = {
                    fields: [
                        'address_components',
                        'formatted_address',
                        'geometry',
                        'place_id',
                        'name',
                    ],
                };
                autocompleteRef.current = new google.maps.places.Autocomplete(
                    inputRef.current,
                    options
                );
                autocompleteRef.current.addListener('place_changed', () => {
                    const place = autocompleteRef.current.getPlace();
                    const formatted = place?.formatted_address || inputRef.current?.value || '';
                    const components: Record<string, string> = {};
                    (place?.address_components || []).forEach((c: any) => {
                        const type = c.types?.[0];
                        if (type) components[type] = c.long_name;
                    });
                    let derived: any = formatted;
                    if (mode === 'city') {
                        derived = components.locality || components.sublocality || components.administrative_area_level_2 || '';
                    } else if (mode === 'state') {
                        derived = components.administrative_area_level_1 || '';
                    } else if (mode === 'postal-code') {
                        derived = components.postal_code || '';
                    } else if (mode === 'address-line') {
                        const street = [components.street_number, components.route].filter(Boolean).join(' ');
                        derived = street || formatted;
                    }
                    onChange(derived || formatted || '');

                    const locationData = place?.geometry?.location
                        ? {
                            lat: place.geometry.location.lat(),
                            lng: place.geometry.location.lng(),
                        }
                        : undefined;

                    autoFillCallbackRef.current?.({
                        formattedAddress: formatted,
                        components,
                        value: derived || formatted || '',
                        location: locationData,
                    });
                });
            })
            .catch(() => {
                setInitError('Failed to load Google Maps script');
            });
        return () => {
            cancelled = true;
        };
    }, [mode]);

    const displayValue = typeof value === 'string' ? value : (value as any)?.value || (value as any)?.formattedAddress || '';

    return (
        <div style={{ position: 'relative' }}>
            <AutocompleteInput
                theme={theme}
                type="text"
                placeholder={placeholder || 'Enter address'}
                value={displayValue}
                ref={inputRef as any}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => onChange(e.target.value)}
                disabled={disabled}
                autoComplete="off"
            />
            {/* Debug overlay for troubleshooting */}
            {initError && (
                <div style={{ position: 'absolute', top: 0, left: 0, right: 0, background: '#fffbe6', color: '#ad6800', fontSize: 12, zIndex: 10, padding: 4, border: '1px solid #ffe58f', borderRadius: 4 }}>
                    Autocomplete not active: {initError}
                </div>
            )}
        </div>
    );
};